import React, { useEffect, useRef, useState } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useAuth } from '../../hooks/useAuth';
import { useAutoLogout } from '../../hooks/useAutoLogout';
import { LoginForm } from './LoginForm';
import { LoadingSpinner } from './LoadingSpinner';
import { SuperAdminProvider } from '../../contexts/SuperAdminContext';

// .lottie format (required by DotLottieReact for reliable playback)
const WELCOME_LOTTIE_SRC = 'https://lottie.host/a4172739-2b99-4f48-bd62-a85c97d86647/OaHsjwgNZU.lottie';

interface AuthWrapperProps {
  children: React.ReactNode;
}

export function AuthWrapper({ children }: AuthWrapperProps) {
  const { user, loading, sessionError } = useAuth();
  const [showWelcome, setShowWelcome] = useState(false);
  const welcomeTimerRef = useRef<number | null>(null);
  const lastUserIdRef = useRef<string | null>(null);
  // First name only (no surname) for welcome
  const firstName =
    user?.user_metadata?.first_name ||
    (user?.user_metadata?.full_name ? String(user.user_metadata.full_name).trim().split(/\s+/)[0] : null) ||
    (user?.email ? user.email.split('@')[0] : null) ||
    'there';
  
  // Initialize auto-logout with 60 minute timeout
  useAutoLogout({
    timeoutMinutes: 60,
    showWarning: true,
    warningMinutes: 5
  });

  useEffect(() => {
    if (!user) {
      if (lastUserIdRef.current) {
        sessionStorage.removeItem(`welcome_shown_${lastUserIdRef.current}`);
        lastUserIdRef.current = null;
      }
      setShowWelcome(false);
      if (welcomeTimerRef.current) {
        window.clearTimeout(welcomeTimerRef.current);
        welcomeTimerRef.current = null;
      }
      return;
    }

    const userId = user.id;
    lastUserIdRef.current = userId;
    const storageKey = `welcome_shown_${userId}`;
    if (sessionStorage.getItem(storageKey)) {
      setShowWelcome(false);
      return;
    }

    sessionStorage.setItem(storageKey, '1');
    setShowWelcome(true);
    if (welcomeTimerRef.current) {
      window.clearTimeout(welcomeTimerRef.current);
    }
    welcomeTimerRef.current = window.setTimeout(() => setShowWelcome(false), 5000);
    return () => {
      if (welcomeTimerRef.current) {
        window.clearTimeout(welcomeTimerRef.current);
        welcomeTimerRef.current = null;
      }
    };
  }, [user]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <LoginForm sessionError={sessionError} />;
  }

  return (
    <SuperAdminProvider>
      {children}
      {showWelcome && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50/80 to-sky-100">
          {/* Animated background elements - blue accents (same as client portal loading) */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-300/25 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-300/25 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s', animationDelay: '1s' }} />
            <div className="absolute top-1/2 left-1/2 w-64 h-64 -translate-x-1/2 -translate-y-1/2 bg-sky-200/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s', animationDelay: '0.5s' }} />
          </div>
          {/* Card: same style as client portal loading (white panel, blue border) */}
          <div className="relative z-10 mx-6 w-full max-w-sm sm:max-w-md rounded-3xl overflow-hidden text-center animate-in fade-in duration-500 bg-white/90 backdrop-blur-md shadow-2xl shadow-blue-500/10 border border-blue-100/60">
            <div className="relative px-6 py-10">
              {/* Lottie - bigger loading animation */}
              <div className="mx-auto w-[320px] h-[260px] sm:w-[380px] sm:h-[300px] flex items-center justify-center [&_canvas]:!max-w-full [&_canvas]:!max-h-full [&_canvas]:!object-contain">
                <DotLottieReact
                  src={WELCOME_LOTTIE_SRC}
                  loop
                  autoplay
                />
              </div>
              {/* Text - dark on light card to match client portal */}
              <h1 className="mt-6 text-2xl sm:text-3xl font-extrabold tracking-tight bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 bg-clip-text text-transparent">
                Welcome, {firstName}
              </h1>
              <p className="mt-2 text-sm sm:text-base font-medium text-slate-600">
                Your modern training command center.
              </p>
              <div className="mt-6 flex items-center justify-center gap-2">
                <span className="h-2 w-2 rounded-full bg-blue-500 animate-bounce" />
                <span className="h-2 w-2 rounded-full bg-indigo-500 animate-bounce [animation-delay:120ms]" />
                <span className="h-2 w-2 rounded-full bg-blue-500 animate-bounce [animation-delay:240ms]" />
              </div>
            </div>
          </div>
        </div>
      )}
    </SuperAdminProvider>
  );
}